import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Button } from "../components/ui/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { ArrowLeft } from "lucide-react";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { fetchCustomerById, fetchCustomerOrders } from "../store/actions/customerActions";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const Title = styled.h2`
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Muted = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

export default function CustomerOrderHistory() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customer, customerOrders, loading, error } = useSelector((state) => state.customer);
  const user = useSelector((state) => state.auth?.user);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerById(customerId));
      dispatch(fetchCustomerOrders(customerId));
    }
  }, [dispatch, customerId]);

  const customerName = customer?.name || customer?.email || "Customer";
  const customerEmail = customer?.email || "—";
  const addresses = customer?.addresses || [];
  const defaultIdx = Math.min(customer?.defaultAddressIndex ?? 0, addresses.length - 1);
  const defaultAddr = defaultIdx >= 0 ? addresses[defaultIdx] : null;
  const baseCurrency = "USD";
  const fmt = (v, cur) => (v != null && typeof v === "number" ? (rates && (cur || baseCurrency) !== currency ? formatCurrency(ratesService.convert(v, cur || baseCurrency, currency, rates), currency) : formatCurrency(v, cur || baseCurrency)) : "—");

  const formatAddr = (a) => {
    if (!a) return "";
    const parts = [a.address1, a.address2, a.city, a.state, a.zip, a.country].filter(Boolean);
    return parts.join(", ");
  };

  if (loading && !customer && !customerOrders?.length) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/customers")}><ArrowLeft size={16} /> Back to Customers</Button>
        <p style={{ color: "#6b7280" }}>Loading...</p>
      </div>
    );
  }
  if (error && !customer) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/customers")}><ArrowLeft size={16} /> Back to Customers</Button>
        <p style={{ color: "#dc2626" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
        <Button
          $variant="ghost"
          $size="sm"
          onClick={() => navigate("/customers")}
        >
          <ArrowLeft size={16} />
          Back to Customers
        </Button>
        <Title>{customerName}'s History</Title>
        <Muted>{customerEmail}</Muted>
        </div>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
        </select>
      </div>

      <Card style={{ padding: 20 }}>
        <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 700 }}>Customer information</h3>
        <div style={{ display: "grid", gap: 8 }}>
          {customer?.phone && <p style={{ margin: 0, fontSize: 14 }}>Phone: {customer.phone}</p>}
          {customer?.location && <p style={{ margin: 0, fontSize: 14 }}>Location: {customer.location}</p>}
          {customer?.status && <p style={{ margin: 0, fontSize: 14 }}>Status: {customer.status}</p>}
          {customer?.tags?.length > 0 && <p style={{ margin: 0, fontSize: 14 }}>Tags: {customer.tags.join(", ")}</p>}
          {customer?.totalSpent != null && <p style={{ margin: 0, fontSize: 14 }}>Total spent: {fmt(customer.totalSpent)}</p>}
        </div>
        {addresses.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
            <h4 style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 600 }}>Addresses</h4>
            {addresses.map((a, i) => (
              <div key={i} style={{ marginBottom: 8, fontSize: 14, color: "#374151" }}>
                {defaultIdx === i && <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginRight: 6 }}>Default</span>}
                {formatAddr(a)}
                {a.phone && <span style={{ marginLeft: 8, color: "#6b7280" }}> · {a.phone}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="deals">Deals</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card style={{ marginTop: 16, padding: 20 }}>
            {error && <p style={{ margin: "0 0 12px", color: "#dc2626", fontSize: 14 }}>{error}</p>}
            {loading && (!customerOrders || customerOrders.length === 0) ? (
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>Loading orders...</p>
            ) : (customerOrders && customerOrders.length > 0) ? (
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead style={{ textAlign: "right" }}>Total</TableHead>
                      <TableHead>Store</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerOrders.map((ord) => (
                      <TableRow key={ord._id || ord.id}>
                        <TableCell style={{ fontWeight: 600 }}>#{ord.orderId || ord._id}</TableCell>
                        <TableCell>{ord.createdAt ? new Date(ord.createdAt).toLocaleDateString() : ord.date || "—"}</TableCell>
                        <TableCell>{ord.status || "—"}</TableCell>
                        <TableCell style={{ textAlign: "right" }}>{fmt(ord.total, ord.currency)}</TableCell>
                        <TableCell>{ord.storeId?.name || ord.store?.name || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>No orders yet.</p>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
              Comments timeline will be ported here.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
              Customer tasks list will be ported here.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="deals">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
              Deals pipeline view will be ported here.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="conversations">
          <Card style={{ marginTop: 16, padding: 20 }}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
              Conversations history will be ported here.
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

